import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  Description,
  Delete,
  Visibility,
  CheckCircle,
  AccessTime,
  Warning,
  InsertDriveFile,
  PictureAsPdf,
  Image,
  CalendarToday,
  VerifiedUser,
  Error as ErrorIcon,
  FolderOpen
} from '@mui/icons-material';
import api from '../services/api';
import { brand, status as statusColors, text, bg, border, shadow } from '../theme/colors';

const DOCUMENT_TYPES = [
  { value: 'registration_cert', label: 'Business Registration Certificate', icon: '📜', required: true },
  { value: 'tax_clearance', label: 'Tax Clearance Certificate', icon: '🏛️', required: true },
  { value: 'business_license', label: 'Business License', icon: '📋', required: false },
  { value: 'trading_license', label: 'Trading License', icon: '🏪', required: false },
  { value: 'vat_certificate', label: 'VAT Certificate', icon: '💰', required: false },
  { value: 'insurance_certificate', label: 'Insurance Certificate', icon: '🛡️', required: false },
  { value: 'quality_certification', label: 'Quality Certification', icon: '✅', required: false },
  { value: 'professional_membership', label: 'Professional Membership', icon: '🤝', required: false },
  { value: 'other', label: 'Other Document', icon: '📄', required: false }
];

const getVerificationStatusConfig = (verificationStatus) => {
  switch (verificationStatus) {
    case 'Approved':
      return { color: statusColors.success, bg: statusColors.successBg, icon: <CheckCircle fontSize="small" />, label: 'Verified' };
    case 'Pending':
      return { color: statusColors.warning, bg: statusColors.warningBg, icon: <AccessTime fontSize="small" />, label: 'Pending Review' };
    case 'Rejected':
      return { color: statusColors.error, bg: statusColors.errorBg, icon: <ErrorIcon fontSize="small" />, label: 'Rejected' };
    default:
      return { color: statusColors.neutral, bg: statusColors.neutralBg, icon: <AccessTime fontSize="small" />, label: 'Pending' };
  }
};

const getFileIcon = (mimeType) => {
  if (!mimeType) return <InsertDriveFile />;
  if (mimeType.includes('pdf')) return <PictureAsPdf sx={{ color: '#ef4444' }} />;
  if (mimeType.includes('image')) return <Image sx={{ color: '#3b82f6' }} />;
  return <InsertDriveFile sx={{ color: brand.primary }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const MerchantDocumentsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadForm, setUploadForm] = useState({
    documentType: '',
    expiryDate: '',
    file: null
  });

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/merchants/documents');
      if (response.data.success) {
        setDocuments(response.data.data || []);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setDocuments([]);
      } else {
        console.error('Error loading documents:', err);
        setError('Failed to load documents. Please ensure your merchant profile is set up.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.documentType) {
      setError('Please select a document type and file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setError('');

      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('document_type', uploadForm.documentType);
      if (uploadForm.expiryDate) {
        formData.append('expiry_date', uploadForm.expiryDate);
      }

      await api.post('/merchants/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setSuccess('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadForm({ documentType: '', expiryDate: '', file: null });
      loadDocuments();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;

    try {
      setError('');
      await api.delete(`/merchants/documents/${selectedDocument.id}`);
      setSuccess('Document deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
      loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err.response?.data?.message || 'Failed to delete document');
    }
  };

  const getDocTypeLabel = (type) => {
    const found = DOCUMENT_TYPES.find(d => d.value === type);
    return found ? found.label : type;
  };

  const getDocTypeIcon = (type) => {
    const found = DOCUMENT_TYPES.find(d => d.value === type);
    return found ? found.icon : '📄';
  };

  // Group documents by type
  const groupedDocuments = DOCUMENT_TYPES.map(docType => ({
    ...docType,
    documents: documents.filter(d => d.document_type === docType.value)
  }));

  const uploadedTypes = new Set(documents.map(d => d.document_type));
  const requiredMissing = DOCUMENT_TYPES.filter(d => d.required && !uploadedTypes.has(d.value));
  const completionPercent = DOCUMENT_TYPES.filter(d => d.required).length > 0
    ? Math.round((DOCUMENT_TYPES.filter(d => d.required && uploadedTypes.has(d.value)).length / DOCUMENT_TYPES.filter(d => d.required).length) * 100)
    : 100;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: brand.primary }} />
          <Typography variant="body1" sx={{ mt: 2, color: text.secondary }}>Loading documents...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ color: text.secondary }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary, letterSpacing: '-0.02em' }}>
              Business Documents
            </Typography>
            <Typography variant="body2" sx={{ color: text.secondary, mt: 0.5 }}>
              Upload and manage your business verification documents
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{
            background: brand.gradient,
            fontWeight: 600,
            px: 3,
            py: 1.2,
            borderRadius: 2,
            boxShadow: shadow.md,
            transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s',
            '&:hover': {
              background: brand.gradientHover,
              transform: 'translateY(-2px)',
              boxShadow: shadow.lg
            }
          }}
        >
          Upload Document
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Compliance Progress */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          border: `1px solid ${border.light}`,
          borderRadius: 3,
          background: completionPercent === 100 ? statusColors.successBg : bg.subtle
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VerifiedUser sx={{ color: completionPercent === 100 ? statusColors.success : brand.primary }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: text.primary }}>
              Verification Progress
            </Typography>
          </Box>
          <Chip
            label={`${completionPercent}% Complete`}
            sx={{
              fontWeight: 600,
              bgcolor: completionPercent === 100 ? statusColors.success : brand.primary,
              color: '#fff'
            }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={completionPercent}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: `${brand.primary}15`,
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: completionPercent === 100 ? statusColors.success : brand.gradient
            }
          }}
        />
        {requiredMissing.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: text.secondary, mr: 1 }}>Missing:</Typography>
            {requiredMissing.map(doc => (
              <Chip
                key={doc.value}
                label={doc.label}
                size="small"
                icon={<Warning sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: statusColors.warningBg,
                  color: statusColors.warningDark,
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: statusColors.warning }
                }}
                onClick={() => {
                  setUploadForm(prev => ({ ...prev, documentType: doc.value }));
                  setUploadDialogOpen(true);
                }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Document Grid */}
      {documents.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: `2px dashed ${border.light}`,
            borderRadius: 3,
            bgcolor: bg.subtle
          }}
        >
          <FolderOpen sx={{ fontSize: 64, color: text.tertiary, mb: 2 }} />
          <Typography variant="h6" sx={{ color: text.primary, fontWeight: 600, mb: 1 }}>
            No Documents Yet
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 3, maxWidth: 400, mx: 'auto' }}>
            Upload your business documents to get verified and unlock premium features. Start with the required documents.
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{
              background: brand.gradient,
              fontWeight: 600,
              px: 4,
              py: 1.2,
              borderRadius: 2,
              '&:hover': { background: brand.gradientHover }
            }}
          >
            Upload Your First Document
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {documents.map((doc) => {
            const statusConfig = getVerificationStatusConfig(doc.verification_status);
            const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();

            return (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: `1px solid ${border.light}`,
                    borderRadius: 3,
                    transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: shadow.md
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Document type header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 40, height: 40,
                          borderRadius: 2,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1.3rem',
                          bgcolor: bg.subtle
                        }}>
                          {getDocTypeIcon(doc.document_type)}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: text.primary, lineHeight: 1.3 }}>
                            {getDocTypeLabel(doc.document_type)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            {getFileIcon(doc.mime_type)}
                            <Typography variant="caption" sx={{ color: text.tertiary }}>
                              {formatFileSize(doc.file_size)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Status badge */}
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={statusConfig.icon}
                        label={statusConfig.label}
                        size="small"
                        sx={{
                          bgcolor: statusConfig.bg,
                          color: statusConfig.color,
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: statusConfig.color }
                        }}
                      />
                      {isExpired && (
                        <Chip
                          icon={<Warning sx={{ fontSize: 14 }} />}
                          label="Expired"
                          size="small"
                          sx={{
                            ml: 1,
                            bgcolor: statusColors.errorBg,
                            color: statusColors.error,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: statusColors.error }
                          }}
                        />
                      )}
                    </Box>

                    {/* Expiry date */}
                    {doc.expiry_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                        <CalendarToday sx={{ fontSize: 14, color: text.tertiary }} />
                        <Typography variant="caption" sx={{ color: isExpired ? statusColors.error : text.secondary }}>
                          {isExpired ? 'Expired' : 'Expires'}: {new Date(doc.expiry_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}

                    {/* Verification notes */}
                    {doc.verification_notes && (
                      <Typography variant="caption" sx={{
                        display: 'block', mb: 2, p: 1.5, borderRadius: 1.5,
                        bgcolor: doc.verification_status === 'Rejected' ? statusColors.errorBg : bg.subtle,
                        color: doc.verification_status === 'Rejected' ? statusColors.error : text.secondary,
                        fontStyle: 'italic'
                      }}>
                        {doc.verification_notes}
                      </Typography>
                    )}

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      {doc.file_url && (
                        <Tooltip title="View Document">
                          <IconButton
                            size="small"
                            onClick={() => window.open(doc.file_url, '_blank')}
                            sx={{ color: brand.primary }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Document">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedDocument(doc);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{ color: statusColors.error }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Upload Business Document
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 3 }}>
            Select the document type and upload a file. Accepted formats: PDF, JPG, PNG (max 10MB).
          </Typography>

          <TextField
            select
            fullWidth
            label="Document Type"
            value={uploadForm.documentType}
            onChange={(e) => setUploadForm(prev => ({ ...prev, documentType: e.target.value }))}
            sx={{ mb: 3 }}
          >
            {DOCUMENT_TYPES.map(type => (
              <MenuItem key={type.value} value={type.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{type.icon}</span>
                  <span>{type.label}</span>
                  {type.required && (
                    <Chip label="Required" size="small" sx={{ ml: 'auto', bgcolor: statusColors.warningBg, color: statusColors.warning, fontWeight: 600, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Expiry Date (Optional)"
            type="date"
            value={uploadForm.expiryDate}
            onChange={(e) => setUploadForm(prev => ({ ...prev, expiryDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 3 }}
          />

          {/* File Drop Zone */}
          <Box
            sx={{
              border: `2px dashed ${uploadForm.file ? brand.primary : border.default}`,
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: uploadForm.file ? `${brand.primary}08` : bg.subtle,
              transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': {
                borderColor: brand.primary,
                bgcolor: `${brand.primary}08`
              }
            }}
            onClick={() => document.getElementById('doc-file-input').click()}
          >
            <input
              id="doc-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (file.size > 10 * 1024 * 1024) {
                    setError('File size must be less than 10MB');
                    return;
                  }
                  setUploadForm(prev => ({ ...prev, file }));
                }
              }}
            />
            {uploadForm.file ? (
              <Box>
                {getFileIcon(uploadForm.file.type)}
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1, color: text.primary }}>
                  {uploadForm.file.name}
                </Typography>
                <Typography variant="caption" sx={{ color: text.secondary }}>
                  {formatFileSize(uploadForm.file.size)} — Click to change
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUpload sx={{ fontSize: 40, color: text.tertiary, mb: 1 }} />
                <Typography variant="body1" sx={{ color: text.secondary, fontWeight: 500 }}>
                  Click to select a file
                </Typography>
                <Typography variant="caption" sx={{ color: text.tertiary }}>
                  PDF, JPG, PNG up to 10MB
                </Typography>
              </Box>
            )}
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{
                height: 6, borderRadius: 3,
                '& .MuiLinearProgress-bar': { background: brand.gradient }
              }} />
              <Typography variant="caption" sx={{ color: text.secondary, mt: 0.5, display: 'block', textAlign: 'center' }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !uploadForm.file || !uploadForm.documentType}
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
            sx={{
              background: brand.gradient,
              fontWeight: 600,
              '&:hover': { background: brand.gradientHover }
            }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Document</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this {selectedDocument ? getDocTypeLabel(selectedDocument.document_type) : 'document'}?
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary, mt: 1 }}>
            This action cannot be undone. You may need to re-upload for verification.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MerchantDocumentsPage;
